import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import TaskModal from './components/TaskModal';
import TaskLogModal from './components/TaskLogModal';
import { mockTasks, mockTaskLogs } from './mockData';
import type { Task } from './types';

export default function SystemTasksPage() {
  const [tasks, setTasks] = useState(mockTasks);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>();
  const [selectedTaskName, setSelectedTaskName] = useState('');

  const handleToggleStatus = (taskId: number) => {
    setTasks(
      tasks.map((task) =>
        task.id === taskId
          ? {
              ...task,
              status: task.status === 'enabled' ? ('disabled' as const) : ('enabled' as const)
            }
          : task
      )
    );
  };

  const handleViewLogs = (task: Task) => {
    setSelectedTaskName(task.name);
    setIsLogModalOpen(true);
  };

  const handleEdit = (task: Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedTask(undefined);
    setIsModalOpen(true);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">定时任务管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理系统定时执行的任务</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-5 py-2.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            创建任务
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                    任务名称
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                    任务类型
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                    执行周期
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                    下次执行时间
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                    最后执行
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                    状态
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 whitespace-nowrap">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      <div>
                        <div className="font-medium">{task.name}</div>
                        <div className="text-xs text-gray-500">{task.description}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {task.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {task.cronExpression}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {task.nextExecutionTime}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              task.lastExecutionResult === 'success'
                                ? 'bg-green-500'
                                : 'bg-red-500'
                            }`}
                          ></span>
                          <span className="text-gray-900">{task.lastExecutionTime}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          耗时 {task.executionDuration}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(task.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                          task.status === 'enabled' ? 'bg-teal-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            task.status === 'enabled' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(task)}
                          className="text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          编辑
                        </button>
                        <button className="text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap">
                          <i className="ri-play-line mr-1"></i>
                          立即执行
                        </button>
                        <button
                          onClick={() => handleViewLogs(task)}
                          className="text-gray-600 hover:text-gray-700 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-list-line mr-1"></i>
                          日志
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <TaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        task={selectedTask}
      />

      <TaskLogModal
        isOpen={isLogModalOpen}
        onClose={() => setIsLogModalOpen(false)}
        logs={mockTaskLogs}
        taskName={selectedTaskName}
      />
    </MainLayout>
  );
}
